// Chụp DOM thật (lá số / quẻ) thành PNG, đóng khung thương hiệu Bói Đê (header
// logo + footer CTA). Dynamic import html-to-image (cài qua `bun install`).

import logoIconUrl from "@/assets/logo-icon.png";

async function loadHtmlToImage() {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore html-to-image được cài qua bun install (xem package.json)
  return import("html-to-image");
}

let logoPromise: Promise<HTMLImageElement | null> | null = null;
/** Load logo Bói Đê (bundled, same-origin → không taint canvas). Cache lại. */
function loadLogo(): Promise<HTMLImageElement | null> {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = logoIconUrl;
  });
  return logoPromise;
}

// Loại phần tử có [data-share-exclude] (vd nút bấm) khỏi ảnh chụp.
function captureFilter(node: unknown): boolean {
  return !(node instanceof HTMLElement && node.hasAttribute("data-share-exclude"));
}

/**
 * Chụp `node` (visual lá số / quẻ) → PNG có khung thương hiệu (header + footer).
 * Tạm gắn class `share-capturing` để tắt backdrop-filter (html-to-image render
 * backdrop-filter thành vùng tối che nội dung).
 */
export async function composeShareImage(node: HTMLElement): Promise<Blob> {
  const htmlToImage = await loadHtmlToImage();

  // Phát hiện chế độ sáng/tối hiện tại của trang web để sinh bảng màu đồng bộ
  const isDark = document.documentElement.classList.contains("dark");
  const BG = isDark ? "#0a0a0f" : "#fdfbf7";
  const GOLD = isDark ? "#e5b869" : "#9c7438";
  const TEXT_MUTED = isDark ? "rgba(229, 184, 105, 0.7)" : "rgba(156, 116, 56, 0.8)";
  const BORDER_COLOR = isDark ? "rgba(229, 184, 105, 0.28)" : "rgba(156, 116, 56, 0.3)";
  const TEXT_FOOTER = isDark ? "#e5b869" : "#8c6239";

  // Đảm bảo node khi được chụp sẽ khớp với theme sáng/tối hiện tại.
  // Ép class "dark" vào node để kích hoạt Tailwind dark: styles trong iframe của html-to-image.
  const wasDark = node.classList.contains("dark");
  if (isDark) {
    node.classList.add("dark");
  } else {
    node.classList.remove("dark");
  }

  // Tìm tất cả các element cha có thể gây che giấu (overflow: hidden/auto), giới hạn chiều cao (max-height)
  // hoặc có thuộc tính transform scale làm thay đổi kích thước đo lường của node.
  // Tạm thời hiển thị visible/auto/none để html-to-image có thể vẽ đầy đủ kích thước thật của lá số.
  const elementsToReset: { element: HTMLElement; originalStyle: string }[] = [];
  let current: HTMLElement | null = node;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const hasOverflow = style.overflow === "hidden" || style.overflowY === "hidden" || style.overflow === "auto" || style.overflowY === "auto" || style.overflow === "scroll" || style.overflowY === "scroll";
    const hasHeightConstraint = style.maxHeight !== "none" || (style.height !== "auto" && style.height !== "100%" && style.height !== "initial" && style.height !== "inherit");
    const hasTransform = style.transform !== "none";

    if (hasOverflow || hasHeightConstraint || hasTransform) {
      elementsToReset.push({
        element: current,
        originalStyle: current.getAttribute("style") || "",
      });

      if (hasOverflow) {
        current.style.setProperty("overflow", "visible", "important");
        current.style.setProperty("overflow-y", "visible", "important");
      }
      if (hasHeightConstraint) {
        current.style.setProperty("height", "auto", "important");
        current.style.setProperty("max-height", "none", "important");
      }
      if (hasTransform) {
        current.style.setProperty("transform", "none", "important");
      }
    }
    current = current.parentElement;
  }

  node.classList.add("share-capturing");
  let top: HTMLCanvasElement;
  try {
    const width = node.offsetWidth;
    const height = node.offsetHeight;

    top = await htmlToImage.toCanvas(node, {
      pixelRatio: 3,
      cacheBust: true,
      backgroundColor: BG,
      filter: captureFilter,
      width,
      height,
      style: {
        transform: "none",
        transformOrigin: "top left",
        margin: "0",
        padding: "0",
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: BG,
      },
    });
  } finally {
    node.classList.remove("share-capturing");
    // Khôi phục lại class dark ban đầu của node
    if (wasDark) {
      node.classList.add("dark");
    } else {
      node.classList.remove("dark");
    }
    // Khôi phục lại style gốc của các phần tử cha đã được sửa đổi
    for (const { element, originalStyle } of elementsToReset) {
      if (originalStyle) {
        element.setAttribute("style", originalStyle);
      } else {
        element.removeAttribute("style");
      }
    }
  }

  // Phóng lên tối thiểu ~1200px chiều rộng để FB/Zalo hiện preview lớn.
  const MIN_W = 1200;
  const scale = top.width < MIN_W ? MIN_W / top.width : 1;
  const W = Math.round(top.width * scale);
  const imgH = Math.round(top.height * scale);
  const u = W / 1200; // hệ số tỉ lệ font/padding theo chiều rộng

  const headerH = Math.round(150 * u);
  const footerH = Math.round(92 * u);
  const sideX = Math.round(60 * u);

  const out = document.createElement("canvas");
  const ctx = out.getContext("2d");
  if (!ctx) {
    const blob = await new Promise<Blob | null>((r) => top.toBlob(r, "image/png"));
    if (!blob) throw new Error("Không tạo được ảnh");
    return blob;
  }

  out.width = W;
  out.height = headerH + imgH + footerH;

  // Nền + visual
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(top, 0, headerH, W, imgH);

  const logo = await loadLogo();

  // ===== Header: logo + "BÓI ĐÊ" + tagline, căn giữa =====
  const brand = "BÓI ĐÊ";
  const titleFont = Math.round(46 * u);
  ctx.font = `${titleFont}px Georgia, serif`;
  ctx.textBaseline = "middle";
  const titleW = ctx.measureText(brand).width;
  const logoSize = logo ? Math.round(72 * u) : 0;
  const gap = logo ? Math.round(16 * u) : 0;
  const blockW = logoSize + gap + titleW;
  const sx = Math.round((W - blockW) / 2);
  const hcy = Math.round(headerH * 0.45);
  if (logo) ctx.drawImage(logo, sx, Math.round(hcy - logoSize / 2), logoSize, logoSize);
  ctx.fillStyle = GOLD;
  ctx.textAlign = "left";
  ctx.fillText(brand, sx + logoSize + gap, hcy);

  ctx.fillStyle = TEXT_MUTED;
  ctx.font = `${Math.round(19 * u)}px Georgia, serif`;
  ctx.textAlign = "center";
  ctx.fillText("H U Y Ề N   H Ọ C   Đ Ô N G   P H Ư Ơ N G", W / 2, Math.round(headerH * 0.8));

  // đường kẻ dưới header
  ctx.strokeStyle = BORDER_COLOR;
  ctx.lineWidth = Math.max(1, Math.round(1.5 * u));
  ctx.beginPath();
  ctx.moveTo(sideX, headerH);
  ctx.lineTo(W - sideX, headerH);
  ctx.stroke();

  // ===== Footer: CTA =====
  const fy = headerH + imgH;
  ctx.beginPath();
  ctx.moveTo(sideX, fy);
  ctx.lineTo(W - sideX, fy);
  ctx.stroke();
  ctx.fillStyle = TEXT_FOOTER;
  ctx.font = `${Math.round(26 * u)}px Georgia, serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("✦  boide.online  ·  Tự xem lá số & gieo quẻ miễn phí  ✦", W / 2, fy + footerH / 2);

  const blob = await new Promise<Blob | null>((r) => out.toBlob(r, "image/png"));
  if (!blob) throw new Error("Không tạo được ảnh");
  return blob;
}

/** Tải file blob về máy với tên cho trước. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
